import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import CreateFormModal from "./CreateFormModal";
import EditFormModal from "./EditFormModal";
import "./Home.css";

function Home() {
  const [forms, setForms] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedFormIds, setSelectedFormIds] = useState([]);
  const [editingForm, setEditingForm] = useState(null);
  const [showIntegrationModal, setShowIntegrationModal] = useState(false);
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [mappingFormId, setMappingFormId] = useState(null);
  const navigate = useNavigate();
  const [selectedSource, setSelectedSource] = useState("Webform");

  useEffect(() => {
    fetchForms();
  }, []);

  // Fetch Forms from API
  const fetchForms = async () => {
    try {
      const token = localStorage.getItem("token");

      const response = await fetch("https://leadbankapi.gensparc.com/api/forms/", {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch forms");
      }

      const data = await response.json();
      setForms(data.filter((form) => form.name && form.name.trim() !== ""));
      setSelectedFormIds([]);
    } catch (error) {
      console.error("Error fetching forms:", error);
    }
  };

  // Open & Close Modals
  const openCreateModal = () => setShowCreateModal(true);
  const closeCreateModal = () => setShowCreateModal(false);
  const openEditModal = (form, e) => {
    e.stopPropagation();
    setEditingForm(form);
    setShowEditModal(true);
  };
  const closeEditModal = () => setShowEditModal(false);
  const openIntegrationModal = (e) => {
    e.stopPropagation();
    setShowIntegrationModal(true);
  };
  const closeIntegrationModal = () => setShowIntegrationModal(false);

  // Open Field Mapping Modal
  const openFieldMapping = (formId, e) => {
    e.stopPropagation();
    setMappingFormId(formId);
    setShowMappingModal(true);
  };
  const closeFieldMapping = () => setShowMappingModal(false);

  // Form Actions
  const handleFormCreated = () => fetchForms();
  const handleFormUpdated = () => {
    fetchForms();
    closeEditModal();
  };

  // Checkbox Handler
  const handleCheckboxChange = (formId, checked) => {
    setSelectedFormIds((prev) =>
      checked ? [...prev, formId] : prev.filter((id) => id !== formId)
    );
  };

  // Delete Selected Forms
  const removeForms = async () => {
    if (selectedFormIds.length === 0) {
      alert("Please select at least one form to remove.");
      return;
    }
  
    const confirmDelete = window.confirm("Are you sure you want to delete the selected form(s)?");
  
    if (!confirmDelete) {
      return; // Exit if user cancels
    }
  
    try {
      const token = localStorage.getItem("token");
      for (const id of selectedFormIds) {
        await fetch(`https://leadbankapi.gensparc.com/api/forms/${id}`, {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      fetchForms();
    } catch (error) {
      console.error("Error removing form(s):", error);
    }
  };
  

  // Navigate to Form Builder
  const openBuilder = (formId) => navigate(`/builder/${formId}`);


// Function to go to Dialer Logs with specific Form ID
const openDialerLogs = (formId, e) => {
  e.stopPropagation();
  navigate(`/dialer-logs/${formId}`); // Pass formId as URL param
};

  // Share Form via Clipboard
  const shareForm = (e, formId) => {
    e.stopPropagation();
    const shareLink = `${window.location.origin}/form/${formId}`;
    navigator.clipboard.writeText(shareLink)
      .then(() => alert("Share link copied to clipboard!"))
      .catch(() => alert("Failed to copy share link."));
  };

  // Logout Handler
  const handleLogout = () => {
    localStorage.clear();
    navigate("/");
  };

  //Dropdown for change source
  const handleSourceChange = (e) => {
    const selected = e.target.value;
    setSelectedSource(selected);

    if (selected === "Facebook") {
      navigate("/leads");
    } else if (selected === "Web Form") {
      navigate("/home");
    }
  };

  return (
    <div id="home-page" className="home-container">
       <div className="source">
        <select id="source-select" className="formsource-dropdown" value={selectedSource} onChange={handleSourceChange}>
        
    <option value="Web Form">Web Form</option>

          <option value="Facebook">Facebook</option>
        </select>
      </div>
      {/* Content Wrapper */}
      <div className="content-wrapper">
        <main className="main-content">
          {/* Topbar Buttons */}
          <div className="main-topbar">
            <button className="btn create-btn" onClick={openCreateModal}>
              Create New Form
            </button>
            <button className="btn remove-btn" onClick={removeForms} disabled={selectedFormIds.length === 0}>
              Remove Form
            </button>
          </div>

          {/* Forms Table */}
          <div className="table-container">
            {forms.length > 0 ? (
              <table className="forms-table">
                <thead>
                  <tr>
                    <th>Select</th>
                    <th>Form Name</th>
                    <th>Company Name</th>
                    <th>Company Image</th>
                    <th>Form ID</th>
                    <th>Edit</th>
                    <th>Share</th>
                    <th>Integrate</th>
                    <th>Report</th>
                  </tr>
                </thead>
                <tbody>
                  {forms.map((form) => (
                    <tr key={form.id} className="form-row" onClick={() => openBuilder(form.id)}>
                      <td onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedFormIds.includes(form.id)}
                          onChange={(e) => handleCheckboxChange(form.id, e.target.checked)}
                        />
                      </td>
                      <td>{form.name}</td>
                      <td>{form.company_name}</td>
                      <td>
                        {form.company_image ? (
                          <img
                            src={`https://leadbankapi.gensparc.com${form.company_image}`}
                            alt="Company"
                            className="company-thumbnail"
                          />
                        ) : "No Image"}
                      </td>
                      <td>{form.id}</td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <button className="edit-btn" onClick={(e) => openEditModal(form, e)} title="Edit Form">
                          ✎
                        </button>
                      </td>
                      <td onClick={(e) => shareForm(e, form.id)}>
                        <button className="share-button" title="Share Form">🔗</button>
                      </td>
                      <td>
                        <button
                          className="integration-button"
                          title="Integrate CRM"
                          onClick={(e) => openFieldMapping(form.id, e)}
                        >
                          ⚙️
                        </button>
                      </td>
                      <td>
    <button
        className="logs-button"
        title="View Logs"
        onClick={(e) => openDialerLogs(form.id, e)}
    >
        📜
    </button>
</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="no-forms">No forms available.</p>
            )}
          </div>
        </main>
      </div>

      {/* Field Mapping Modal */}
      {showMappingModal && (
        <div className="integration-modal">
          <div className="modal-content">
            <h2>Map Form Fields</h2>
            <button className="modal-close" onClick={closeFieldMapping}>✖</button>
            <div className="integration-options">
              <button
                className="integration-option"
                onClick={() => navigate(`/dialer/${mappingFormId}`)}
              >
                Go to Field Mapping
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      <CreateFormModal show={showCreateModal} onClose={closeCreateModal} onFormCreated={handleFormCreated} />
      {editingForm && <EditFormModal show={showEditModal} onClose={closeEditModal} formData={editingForm} onFormUpdated={handleFormUpdated} />}
    </div>
  );
}

export default Home;
